import { ChecksumAddress, LINKS, ProtocolInstance } from '@silo/types';
import { Button, Dots, Modal, PlainLink } from '@silo/ui';
import { noop } from '@silo/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TermsOfUseAgreementKey, postAgreement, termsOfUseAgreementKeyName } from 'app/queries/terms-of-use';
import { TermsOfUseAction } from 'data/terms-of-use/types';

interface TermsOfUseModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  user: ChecksumAddress;
  onSuccess?: () => void;
  protocolInstance: ProtocolInstance;
  market: ChecksumAddress;
  action: TermsOfUseAction;
}

export function TermsOfUseModal({
  isModalOpen,
  closeModal,
  user,
  onSuccess = noop,
  protocolInstance,
  market,
  action,
}: TermsOfUseModalProps) {
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: postAgreement,
    onSuccess: () => {
      const termsOfUseAgreementKey: TermsOfUseAgreementKey = [termsOfUseAgreementKeyName, user];
      queryClient.setQueryData(termsOfUseAgreementKey, true);

      queryClient.invalidateQueries({ queryKey: [termsOfUseAgreementKeyName] });

      closeModal();
      onSuccess();
    },
  });

  return (
    <Modal isOpen={isModalOpen} closeModal={closeModal} notDismissable>
      <div className="flex flex-col gap-10">
        <Content />

        <div className="flex items-center gap-5">
          {isLoading ? (
            <Button variant="primary" disabled className="bg-shale-700">
              <Dots>Saving</Dots>
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                mutate({ protocolInstance, user, market, action });
              }}
            >
              Agree and continue
            </Button>
          )}

          <Button
            variant="outline"
            className="border-shale-600 sm:border-none text-shale-300 hover:text-shale-200"
            onClick={() => closeModal()}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const FULL_SAFE_HARBOR_AGREEMENT = 'https://github.com/security-alliance/safe-harbor/blob/main/documents/agreement.pdf';
const ADOPTION_TX = 'https://etherscan.io/tx/0xf204069f4ec135423ad45dc451674334fdb03c876e4ae28290cee49742f50a8c';

function Content(): JSX.Element {
  return (
    <div className="flex flex-col gap-5 text-lg text-shale-100">
      <p>
        SiloDAO has updated its Terms of Use to adopt the{' '}
        <PlainLink external href={LINKS.SAFE_HARBOR_AGREEMENT}>
          Safe Harbor Agreement
        </PlainLink>
        . Please agree to the new terms to continue using the app.
      </p>

      <p>
        Read{' '}
        <PlainLink external href={LINKS.TERMS_OF_USE}>
          section 20
        </PlainLink>
        , <span className="italic">Consent to Attempted Eligible Funds Rescues and Payment of Bounties</span>, to learn
        about the added terms.
      </p>

      <p>
        Read the entire{' '}
        <PlainLink external href={FULL_SAFE_HARBOR_AGREEMENT}>
          Safe Harbor Agreement
        </PlainLink>
        . View{' '}
        <PlainLink external href={ADOPTION_TX}>
          adoption transaction
        </PlainLink>
        .
      </p>
    </div>
  );
}
